import { useState, useContext } from 'react';
import { AppContext } from '@/App';
import { Cog, Activity, AlertTriangle, CheckCircle, Clock, Plus, Wrench, Package } from 'lucide-react';

function ProcessingMachines() {
  const { API } = useContext(AppContext);

  // Machine definitions for aluminium scrap processing
  const machines = [
    // Shredders
    { id: 'SHR-01', name: 'Shredder 1', type: 'Shredder', capacity: '2 Ton/hr', status: 'Running', currentMaterial: 'Taldon', processedToday: 12500, target: 16000 },
    { id: 'SHR-02', name: 'Shredder 2', type: 'Shredder', capacity: '2 Ton/hr', status: 'Running', currentMaterial: 'Telic', processedToday: 14200, target: 16000 },
    { id: 'SHR-03', name: 'Shredder 3', type: 'Shredder', capacity: '1.5 Ton/hr', status: 'Maintenance', currentMaterial: null, processedToday: 0, target: 12000 },
    // Breakers
    { id: 'BRK-01', name: 'Breaker 1', type: 'Breaker', capacity: '1 Ton/hr', status: 'Running', currentMaterial: 'Twitch', processedToday: 6800, target: 8000 },
    { id: 'BRK-02', name: 'Breaker 2', type: 'Breaker', capacity: '1 Ton/hr', status: 'Idle', currentMaterial: null, processedToday: 4500, target: 8000 },
    // Balers
    { id: 'BAL-01', name: 'Baler 1', type: 'Baler', capacity: '500 kg/hr', status: 'Running', currentMaterial: 'Talc', processedToday: 3200, target: 4000 },
    { id: 'BAL-02', name: 'Baler 2', type: 'Baler', capacity: '500 kg/hr', status: 'Running', currentMaterial: 'Tally', processedToday: 3800, target: 4000 },
    // Sorting Lines
    { id: 'SRT-01', name: 'Sorting Line 1', type: 'Sorting', capacity: '3 Ton/hr', status: 'Running', currentMaterial: 'Mixed Scrap', processedToday: 18500, target: 24000 },
    { id: 'SRT-02', name: 'Sorting Line 2', type: 'Sorting', capacity: '2 Ton/hr', status: 'Running', currentMaterial: 'Taint Tabor', processedToday: 11200, target: 16000 },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Running': return 'border-green-500 bg-green-50';
      case 'Idle': return 'border-yellow-500 bg-yellow-50';
      case 'Maintenance': return 'border-red-500 bg-red-50';
      default: return 'border-gray-300 bg-gray-50';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Running': return <Activity className="text-green-500 animate-pulse" size={18} />;
      case 'Idle': return <Clock className="text-yellow-500" size={18} />;
      case 'Maintenance': return <AlertTriangle className="text-red-500" size={18} />;
      default: return null;
    }
  };

  const getMachineIcon = (type) => {
    switch (type) {
      case 'Shredder': return <Cog className="text-blue-500" size={24} />;
      case 'Breaker': return <Wrench className="text-orange-500" size={24} />;
      case 'Baler': return <Package className="text-purple-500" size={24} />;
      case 'Sorting': return <Activity className="text-teal-500" size={24} />;
      default: return <Cog className="text-gray-500" size={24} />;
    }
  };

  const runningMachines = machines.filter(m => m.status === 'Running').length;
  const totalProcessed = machines.reduce((sum, m) => sum + m.processedToday, 0);
  const totalTarget = machines.reduce((sum, m) => sum + m.target, 0);

  const machinesByType = {
    Shredder: machines.filter(m => m.type === 'Shredder'),
    Breaker: machines.filter(m => m.type === 'Breaker'),
    Baler: machines.filter(m => m.type === 'Baler'),
    Sorting: machines.filter(m => m.type === 'Sorting'),
  };

  return (
    <div className="space-y-6" data-testid="processing-machines-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Cog className="text-[#1a237e]" size={28} />
            Processing Machines
          </h1>
          <p className="text-gray-600 mt-1">Monitor all scrap processing equipment</p>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-[#1a237e] to-[#283593] rounded-xl p-4 text-white">
          <p className="text-sm opacity-80">Total Machines</p>
          <p className="text-3xl font-bold mt-1">{machines.length}</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-4 text-white">
          <p className="text-sm opacity-80">Running</p>
          <p className="text-3xl font-bold mt-1">{runningMachines}</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <p className="text-sm opacity-80">Processed Today</p>
          <p className="text-3xl font-bold mt-1">{(totalProcessed / 1000).toFixed(1)} T</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
          <p className="text-sm opacity-80">Achievement</p>
          <p className="text-3xl font-bold mt-1">{((totalProcessed / totalTarget) * 100).toFixed(0)}%</p>
        </div>
      </div>

      {/* Status Legend */}
      <div className="flex items-center gap-6 px-4">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
          <span className="text-sm text-gray-600">Running</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
          <span className="text-sm text-gray-600">Idle</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <span className="text-sm text-gray-600">Maintenance</span>
        </div>
      </div>

      {/* Machines by Type */}
      {Object.entries(machinesByType).map(([type, typeMachines]) => (
        <div key={type} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          <div className={`p-4 border-b border-gray-200 ${
            type === 'Shredder' ? 'bg-gradient-to-r from-blue-50 to-indigo-50' :
            type === 'Breaker' ? 'bg-gradient-to-r from-orange-50 to-amber-50' :
            type === 'Baler' ? 'bg-gradient-to-r from-purple-50 to-pink-50' :
            'bg-gradient-to-r from-teal-50 to-cyan-50'
          }`}>
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              {getMachineIcon(type)}
              {type}s ({typeMachines.length} units)
            </h3>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {typeMachines.map((machine) => (
                <div
                  key={machine.id}
                  className={`rounded-xl border-2 p-4 transition hover:shadow-md ${getStatusColor(machine.status)}`}
                  data-testid={`machine-${machine.id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="font-bold text-lg text-gray-900">{machine.name}</span>
                      <p className="text-xs text-gray-500 font-mono">{machine.id}</p>
                    </div>
                    {getStatusIcon(machine.status)}
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Capacity</span>
                      <span className="font-medium text-gray-900">{machine.capacity}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Status</span>
                      <span className={`font-medium ${
                        machine.status === 'Running' ? 'text-green-600' :
                        machine.status === 'Idle' ? 'text-yellow-600' : 'text-red-600'
                      }`}>{machine.status}</span>
                    </div>
                    {machine.currentMaterial && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Processing</span>
                        <span className="font-medium text-blue-600">{machine.currentMaterial}</span>
                      </div>
                    )}
                    <div className="pt-2 border-t border-gray-200">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-500">Today's Progress</span>
                        <span className="font-medium">{(machine.processedToday / 1000).toFixed(1)}T / {(machine.target / 1000).toFixed(0)}T</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${
                            (machine.processedToday / machine.target) >= 0.9 ? 'bg-green-500' :
                            (machine.processedToday / machine.target) >= 0.7 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${Math.min((machine.processedToday / machine.target) * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default ProcessingMachines;
